import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '@environments/environment';

import { ApiResponse } from '@core/types';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { Quotation, QuotationCreate, QuotationDetail, QuotationFilter, QuotationUpdate } from '@core/types/quotation.type';

@Injectable({ providedIn: 'root' })
export class QuotationService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api}/quotations`;

  grid(config: PaginationConfig): Observable<ApiResponse<PaginationResult<Quotation>>> {
    let params = new HttpParams()
      .set('pageNumber', config.pageNumber.toString())
      .set('pageSize', config.pageSize.toString());

    if (config.searchTerm) params = params.set('searchTerm', config.searchTerm);

    const filter = (config.filter || {}) as QuotationFilter;
    if (filter.supplierId) params = params.set('filter.supplierId', filter.supplierId);
    if (filter.status !== undefined) params = params.set('filter.status', String(filter.status));

    return this.http.get<ApiResponse<PaginationResult<Quotation>>>(`${this.baseUrl}/grid`, { params }).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error loading quotations')),
    );
  }

  get(id: string): Observable<ApiResponse<QuotationDetail>> {
    return this.http.get<ApiResponse<QuotationDetail>>(`${this.baseUrl}/${id}`).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error loading quotation')),
    );
  }

  create(data: QuotationCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(this.baseUrl, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error creating quotation')),
    );
  }

  update(data: QuotationUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(this.baseUrl, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error updating quotation')),
    );
  }

  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error deleting quotation')),
    );
  }
}

